import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import { requestUploadFiles } from '@atom/actions/fileActions';
import {
  requestClearFormInstance,
  requestDeleteFormInstanceMedia,
  requestProgressiveUpdateFormInstanceAttribute,
  requestProgressiveUpdateFormInstanceCi,
  requestProgressiveUpdateFormInstanceFhwa,
  requestProgressiveUpdateFormInstanceField,
  requestProgressiveUpdateFormInstancePci,
  retrieveFormInstance,
} from '@atom/actions/formInstanceActions';
import { getFormTemplate } from '@atom/actions/formTemplateActions';
import { retrieveInventorySchemas } from '@atom/actions/inventorySchemaActions';
import { requestPatchMedia } from '@atom/actions/mediaActions';
import { useNavigation } from '@atom/hooks/useNavigation';
import {
  getOrderedFormInstanceMedia,
  getRootSchema,
  updateFormInstanceAttribute,
  updateFormInstanceFhwa,
  updateFormInstanceField,
  updateFormInstanceMedia,
} from '@atom/selectors/formInstanceSelectors';
import { inventorySchemasSelector } from '@atom/selectors/inventorySchemasSelectors';
import {
  CiModuleInspectionUpdate,
  FormInstanceTemplateType,
  FormTemplateType,
  PciModuleDataUpdate,
} from '@atom/types/form';
import { InventorySchemasState, RootState } from '@atom/types/store';

import FormInstanceCanvas from './FormInstanceCanvas';
import FormInstanceHeader from './FormInstanceHeader';
import FormInstancePages from './FormInstancePages';

import classes from './formInstance.module.css';

const initialForm = {
  id: '',
  name: '',
  createdDate: 0,
  pages: [{ name: '', order: [], fields: {} }],
  media: [],
};

const FormInstance = () => {
  const dispatch = useDispatch();
  const [, { params, pathname }] = useNavigation();
  const { id } = params;

  const formTemplate: FormTemplateType = useSelector(
    (state: RootState) => state.formTemplateDetail,
  );

  const formInstance: FormInstanceTemplateType = useSelector(
    (state: RootState) => state.formInstance,
  );

  const loadingFormInstance: boolean = useSelector(
    (state: RootState) => state.loading.loadingFormInstance,
  );

  const savingFormInstance: boolean = useSelector(
    (state: RootState) => state.loading.savingFormInstance,
  );

  const loadingFormTemplate: boolean = useSelector(
    (state: RootState) => state.loading.loadingFormTemplate,
  );

  const inventorySchemas: InventorySchemasState = useSelector(
    (state: RootState) => inventorySchemasSelector(state),
  );

  const preview = pathname?.includes('preview');

  const [selectedPage, setSelectedPage] = useState<number>(0);
  const [form, setForm] = useState<any>(preview ? formTemplate : initialForm);

  useEffect(() => {
    if (preview) {
      dispatch(getFormTemplate(id));
    }

    if (!preview) {
      dispatch(
        retrieveFormInstance({
          formInstanceId: id,
        }),
      );
    }

    if (R.isNil(inventorySchemas) || R.isEmpty(inventorySchemas)) {
      dispatch(
        retrieveInventorySchemas({
          rootSchemas: true,
        }),
      );
    }

    return () => {
      dispatch(requestClearFormInstance());
    };
  }, []);

  useEffect(() => {
    if (!preview && (!loadingFormInstance || R.isEmpty(form.id))) {
      setForm(formInstance);
    }
  }, [formInstance, loadingFormInstance]);

  useEffect(() => {
    if (preview) {
      setForm(formTemplate);
    }
  }, [formTemplate]);

  const removeFile = (mediaId: string, subjectId: string) => {
    const data = {
      mediaId,
      subjectId,
      formInstanceId: form.id,
    };

    dispatch(requestDeleteFormInstanceMedia(data));
  };

  const progressiveUpdateFormInstanceField = (
    fieldId: string,
    body: Object,
  ) => {
    // @ts-ignore
    if (preview || R.isNil(body.value)) {
      return;
    }

    const pageId = form.pages[selectedPage].id;

    const data = {
      formInstanceId: form.id,
      pageId,
      fieldId,
      body,
    };

    dispatch(requestProgressiveUpdateFormInstanceField(data));

    const updatedForm = updateFormInstanceField(form, pageId, fieldId, body);
    setForm(updatedForm);
  };

  const progressiveUpdateFormInstanceAttribute = (
    fieldId: string,
    assetId: string,
    attributeGroupName: string,
    attributeId: string,
    body: Object,
  ) => {
    // @ts-ignore
    if (preview || R.isNil(body.value)) {
      return;
    }

    const pageId = form.pages[selectedPage].id;

    const data = {
      formInstanceId: form.id,
      pageId,
      fieldId,
      assetId,
      attributeId,
      body,
    };

    dispatch(requestProgressiveUpdateFormInstanceAttribute(data));

    const updatedForm = updateFormInstanceAttribute(
      form,
      pageId,
      fieldId,
      assetId,
      attributeGroupName,
      attributeId,
      body,
    );
    setForm(updatedForm);
  };

  const progressiveUpdateFormInstanceFhwa = (
    fieldId: string,
    assetId: string,
    body: Object,
    elementPath: any[],
  ) => {
    // @ts-ignore
    if (preview || R.isNil(body.attributes)) {
      return;
    }

    const pageId = form.pages[selectedPage].id;

    const data = {
      formInstanceId: form.id,
      pageId,
      fieldId,
      assetId,
      body,
    };

    dispatch(requestProgressiveUpdateFormInstanceFhwa(data));

    const updatedForm = updateFormInstanceFhwa(
      form,
      pageId,
      fieldId,
      assetId,
      body,
      elementPath,
    );
    setForm(updatedForm);
  };

  const progressiveUpdateFormInstanceCi = (
    update: CiModuleInspectionUpdate,
  ) => {
    const { fieldId, inspectionIndex, ...updates } = update;

    const formInstanceId = form.id;
    const pageId = form.pages[selectedPage].id;

    if (!preview) {
      dispatch(
        requestProgressiveUpdateFormInstanceCi({
          formInstanceId,
          pageId,
          selectedPage,
          fieldId,
          ...updates,
        }),
      );
    }

    const lens = R.lensPath([
      'pages',
      selectedPage,
      'fields',
      fieldId,
      'inspections',
      inspectionIndex,
    ]);
    setForm(R.over(lens, inspection => ({ ...inspection, ...updates }), form));
  };

  const progressiveUpdateFormInstancePci = (update: PciModuleDataUpdate) => {
    const { fieldId, ...updates } = update;

    const formInstanceId = form.id;
    const pageId = form.pages[selectedPage].id;

    if (!preview) {
      dispatch(
        requestProgressiveUpdateFormInstancePci({
          formInstanceId,
          pageId,
          fieldId,
          ...updates,
        }),
      );
    }

    const lens = R.lensPath(['pages', selectedPage, 'fields', fieldId, 'pci']);
    setForm(R.over(lens, pci => ({ ...pci, ...updates }), form));
  };

  const uploadFiles = (
    files?: any[],
    subjectId?: string,
    subjectType?: string,
    inspectionUpdate?: CiModuleInspectionUpdate,
  ) => {
    const widths = [24, 180, 1440];

    const pageId = form.pages[selectedPage].id;

    const data = {
      files,
      widths,
      subjectId,
      subjectType,
      parentSubjectId: form.id,
      queryParams: {
        parentSubjectId: form.id,
      },
      ...(inspectionUpdate && {
        inspectionUpdate: {
          ...inspectionUpdate,
          pageId,
        },
      }),
    };

    dispatch(requestUploadFiles(data));
  };

  const renameMedia = (mediaId: string, subjectId: string, name: string) => {
    if (preview) {
      return;
    }

    const data = {
      id: mediaId,
      name,
    };

    dispatch(requestPatchMedia(data));

    const updatedForm = updateFormInstanceMedia(form, subjectId, mediaId, name);
    setForm(updatedForm);
  };

  const instanceCount = R.pathOr(0, ['instanceCount'], form);
  const isPublished = R.pathOr(false, ['isPublished'], form);

  const loading = preview ? loadingFormTemplate : loadingFormInstance;
  const rootSchema = getRootSchema(form.schemaId, inventorySchemas);
  const formMedia = form.media ? getOrderedFormInstanceMedia(form) : [];

  return (
    <>
      <FormInstanceHeader
        form={form}
        isPublished={isPublished}
        instanceCount={instanceCount}
        preview={preview}
        savingFormInstance={savingFormInstance}
      />
      <div className={classes.formInstanceContainer}>
        <FormInstancePages
          form={form}
          preview={preview}
          updateSelectedPage={setSelectedPage}
          selectedPage={selectedPage}
        />
        <FormInstanceCanvas
          preview={preview}
          rootSchema={rootSchema}
          uploadFiles={uploadFiles}
          form={form}
          media={formMedia}
          selectedPage={selectedPage}
          loading={loading}
          progressiveUpdateFormInstanceField={
            progressiveUpdateFormInstanceField
          }
          progressiveUpdateFormInstanceAttribute={
            progressiveUpdateFormInstanceAttribute
          }
          progressiveUpdateFormInstanceFhwa={progressiveUpdateFormInstanceFhwa}
          progressiveUpdateFormInstanceCi={progressiveUpdateFormInstanceCi}
          progressiveUpdateFormInstancePci={progressiveUpdateFormInstancePci}
          removeFile={removeFile}
          renameMedia={renameMedia}
          savingFormInstance={savingFormInstance}
        />
      </div>
    </>
  );
};

export default FormInstance;
